import { type CSSProperties, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer } from '@helios/ui';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { qHomeOutstandingDetail, type HomeOutstandingDetailRow } from '@helios/data-access';
import {
  DrawerSummaryBanner,
  DrawerRowCard,
  DrawerFooterButton,
  DrawerEmptyState,
  PatientAvatar,
  OverdueBadge,
} from './shared';
import { useFormatMoney } from '../../hooks/useFormatMoney';

export interface OutstandingDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portalStyle?: CSSProperties;
}

export function OutstandingDrawer({ open, onOpenChange, portalStyle }: OutstandingDrawerProps) {
  const navigate = useNavigate();
  const fmtCompact = useFormatMoney({ compact: true });
  const fmtCurrency = (amount: number) => fmtCompact(Math.round(amount));
  const { data: rows = [] } = usePowerSyncQuery<HomeOutstandingDetailRow>(qHomeOutstandingDetail());

  const totalOutstanding = useMemo(
    () => rows.reduce((sum, r) => sum + (r.grand_total ?? 0), 0),
    [rows],
  );

  // Border color based on overdue severity
  const borderForDays = (days: number) => {
    if (days >= 30) return '#fecaca';
    if (days >= 14) return '#fed7aa';
    return 'var(--border, #e5e7eb)';
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Outstanding"
      description="Unpaid invoices"
      width={520}
      portalStyle={portalStyle}
      footer={
        <DrawerFooterButton
          label="View All Outstanding"
          color="#dc2626"
          onClick={() => {
            // PRD.249 — open the dedicated Outstanding Balances page.
            navigate('/clinic/outstanding');
            onOpenChange(false);
          }}
        />
      }
    >
      <DrawerSummaryBanner
        icon="file-text"
        iconBg="#fef2f2"
        iconColor="#dc2626"
        value={fmtCurrency(totalOutstanding)}
        description={`${rows.length} unpaid invoice${rows.length !== 1 ? 's' : ''}`}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3, 12px)',
          padding: 'var(--sp-4, 16px)',
        }}
      >
        {rows.length === 0 && <DrawerEmptyState message="No outstanding invoices" />}

        {rows.map((row) => (
          <DrawerRowCard key={row.invoice_id} borderColor={borderForDays(row.days_overdue)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3, 12px)' }}>
              <PatientAvatar name={row.patient_name} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm, 13px)',
                    fontWeight: 'var(--font-weight-semi, 600)',
                    color: 'var(--text, #111827)',
                  }}
                >
                  {row.patient_name ?? 'Unknown'}
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs, 12px)',
                    color: 'var(--text-muted, #6b7280)',
                  }}
                >
                  {row.invoice_no ?? '—'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-base, 14px)',
                    fontWeight: 'var(--font-weight-semi, 600)',
                    color: 'var(--text, #111827)',
                  }}
                >
                  {fmtCurrency(row.grand_total)}
                </div>
                <OverdueBadge days={row.days_overdue} />
              </div>
            </div>

            <button
              onClick={() => {
                const base = `/patient/${row.patient_id}/encounters`;
                const path = row.encounter_id
                  ? `${base}/${row.encounter_id}?tab=invoices&invoiceId=${row.invoice_id}`
                  : base;
                navigate(path);
                onOpenChange(false);
              }}
              style={{
                width: '100%',
                height: 32,
                borderRadius: 'var(--radius-md, 10px)',
                border: 'none',
                background: row.days_overdue >= 30 ? '#dc2626' : '#ea580c',
                color: '#fff',
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 'var(--font-weight-semi, 600)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
              }}
            >
              Open Invoice →
            </button>
          </DrawerRowCard>
        ))}
      </div>
    </Drawer>
  );
}
